// Shared catalog card for /models and the image/video discovery grids.
// Neutral surfaces keep attention on the model identity and capabilities.

import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from '@/components/Image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ModelPriceTier from './ModelPriceTier';
import { getPrimaryOutput, isNewModel } from '@/utils/model_catalog';

const ModelCard = ({ model, onPress, onDetailsPress, isLoading, palette, accessibilityLabel }) => {
  const { colors, theme } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const styles = useMemo(() => createStyles(colors, palette || getCatalogPalette(theme)), [colors, theme, palette]);

  if (isLoading) {
    return (
      <View style={styles.container} accessibilityLabel="Loading model" accessibilityState={{ busy: true }}
        {...(Platform.OS === 'web' && { 'aria-busy': true })}>
        <View style={styles.content}>
          <View style={styles.header}>
            <View style={[styles.logo, styles.skeleton]} />
            <View style={[styles.skeleton, styles.skeletonBadge]} />
          </View>
          <View style={[styles.skeleton, styles.skeletonTitle]} />
          <View style={styles.chipRow}>
            <View style={[styles.skeleton, styles.skeletonTag]} />
            <View style={[styles.skeleton, styles.skeletonTag]} />
            <View style={styles.priceTier}>
              <View style={[styles.skeleton, styles.skeletonPrice]} />
            </View>
          </View>
        </View>
      </View>
    );
  }

  if (!model) return null;

  const primary = getPrimaryOutput(model);
  const isNew = isNewModel(model);
  const isPlus = !!model.flags?.isPlusOnly;
  const isOSS = !!model.flags?.isOpenSource;
  // Populated tags can include unresolved references or deleted records.
  const tags = (Array.isArray(model.tags) ? model.tags : [])
    .filter(tag => tag && (tag.name || tag.slug));

  return (
    <View
      style={[
        styles.container,
        hovered && styles.interactive,
        focused && styles.focused,
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel || `Chat with ${model.name}`}
        onPress={() => onPress?.(model.id)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={({ pressed }) => [styles.content, pressed && styles.pressed]}
      >
        <View style={styles.header}>
          <View style={styles.logo}>
            {model.logoPath
              ? <Image source={{ uri: model.logoPath }} style={styles.logoImage} contentFit="cover" />
              : <Icon name={primary.icon} size={24} color={colors.text_color_secondary} />
            }
          </View>
          <View style={styles.statusRow}>
            {isNew && <Text size="xxs" weight="medium" color="secondary" style={styles.newLabel}>New</Text>}
            {isPlus && (
              <View style={styles.statusBadge}>
                <Text size="xxs" weight="bold" color="secondary">Plus</Text>
              </View>
            )}
            {isOSS && (
              <View style={styles.statusBadge}>
                <Text size="xxs" weight="medium" color="secondary">OSS</Text>
              </View>
            )}
          </View>
        </View>

        <Text size="l" weight="bold" color="primary" numberOfLines={1} style={styles.title}>
          {model.name}
        </Text>
        {/* Reserve one row even without tags, keeping pricing aligned. */}
        <View style={styles.chipRow}>
          {tags.slice(0, 3).map(tag => (
            <View key={tag.id || tag.slug || tag.name} style={styles.tagChip}>
              <Text size="xxs" color="secondary" numberOfLines={1} style={styles.tagLabel}>
                {tag.name || tag.slug}
              </Text>
            </View>
          ))}
          <View style={[styles.priceTier, !onDetailsPress && { paddingRight: 0 }]}>
            <ModelPriceTier model={model} variant="plain" />
          </View>
        </View>
      </Pressable>
      {onDetailsPress && <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Details about ${model.name}`}
        onPress={() => onDetailsPress(model.id)}
        hitSlop={4}
        style={({ hovered, pressed }) => [styles.detailsButton, (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary }]}
      >
        <Icon name="circle-info" size={16} color={colors.text_color_secondary} />
      </Pressable>}
    </View>
  );
};

ModelCard.propTypes = {
  model: PropTypes.object,
  company: PropTypes.object,
  onPress: PropTypes.func,
  isLoading: PropTypes.bool,
};

const createStyles = (colors, palette) => StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: palette.surface,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: palette.border,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      transition: 'border-color 160ms ease, background-color 160ms ease',
    }),
  },
  interactive: {
    backgroundColor: palette.hover,
    borderColor: palette.activeBorder,
  },
  focused: {
    borderColor: colors.text_color_link,
  },
  pressed: { opacity: 0.8 },
  content: {
    padding: 20,
    paddingBottom: 18,
    flexGrow: 1,
  },
  header: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  logo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    flexShrink: 0,
    backgroundColor: palette.preview,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logoImage: { width: '100%', height: '100%' },
  statusRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    gap: 6,
  },
  newLabel: { paddingHorizontal: 4, lineHeight: 22 },
  statusBadge: {
    borderWidth: 1,
    borderColor: palette.border,
    paddingHorizontal: 8,
    minHeight: 22,
    borderRadius: 7,
    justifyContent: 'center',
  },
  title: { lineHeight: 24, letterSpacing: -0.3, marginBottom: 8 },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 24,
    marginTop: 8,
  },
  tagChip: {
    flexShrink: 1,
    minWidth: 0,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: palette.preview,
  },
  tagLabel: { textTransform: 'capitalize', lineHeight: 16 },
  priceTier: {
    marginLeft: 'auto',
    paddingLeft: 6,
    paddingRight: 30,
    flexShrink: 0,
  },
  detailsButton: { position: 'absolute', right: 14, bottom: 14, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  skeleton: { backgroundColor: palette.border, borderRadius: 6 },
  skeletonBadge: { width: 42, height: 22 },
  skeletonTitle: { width: '72%', height: 18, marginTop: 3, marginBottom: 11 },
  skeletonTag: { width: 56, height: 24 },
  skeletonPrice: { width: 40, height: 12, marginVertical: 4 },
});

export default React.memo(ModelCard);
